import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Globe, ExternalLink, Copy, Check, MessageSquare, Search } from 'lucide-react';
import { Sidebar, Topbar } from './Dashboard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api, { getStoredUser } from '../api';
import { useToast } from '../hooks/use-toast';
import { RequestsSheet } from '../components/portals/RequestsSheet';

const ago = (iso) => {
  if (!iso) return 'Never';
  const d = Math.floor((Date.now() - new Date(iso)) / 86400000);
  return d <= 0 ? 'Today' : d === 1 ? 'Yesterday' : `${d} days ago`;
};
const Pill = ({ n, cls, label, testid }) => <span className={`inline-flex min-w-[26px] justify-center px-1.5 py-0.5 rounded-full text-xs font-semibold ${n ? cls : 'bg-stone-100 text-stone-400'}`} title={label} data-testid={testid}>{n}</span>;

const Row = ({ c, team, onManager, onRequests }) => {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/portal/${c.portalToken}`;
  const copy = async () => { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <tr className="border-t border-stone-100 hover:bg-stone-50/60 transition-colors" data-testid={`portal-row-${c.id}`}>
      <td className="px-4 py-3"><div className="font-medium text-stone-900">{c.name}</div><div className="text-xs text-stone-500">{c.contact || c.email || c.entityType}</div></td>
      <td className="px-4 py-3">
        <Select value={c.managerId || 'none'} onValueChange={(v) => onManager(c, v === 'none' ? null : v)}>
          <SelectTrigger className="h-8 w-48 text-xs" data-testid={`portal-manager-${c.id}`}><SelectValue/></SelectTrigger>
          <SelectContent><SelectItem value="none">Not assigned (owner)</SelectItem>{team.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
        </Select>
      </td>
      <td className="px-4 py-3 text-xs text-stone-600 whitespace-nowrap" data-testid={`portal-lastseen-${c.id}`}>{ago(c.lastSeen)}</td>
      <td className="px-4 py-3 text-xs text-stone-600 whitespace-nowrap">{ago(c.lastActivity)}</td>
      <td className="px-4 py-3 text-center"><Pill n={c.openRequests} cls="bg-sky-100 text-sky-800" label="Open requests" testid={`portal-open-${c.id}`}/></td>
      <td className="px-4 py-3 text-center"><Pill n={c.unreadMessages} cls="bg-red-100 text-red-700" label="Unread client replies" testid={`portal-unread-${c.id}`}/></td>
      <td className="px-4 py-3 text-center"><Pill n={c.newUploads} cls="bg-emerald-100 text-emerald-800" label="Uploads this week" testid={`portal-uploads-${c.id}`}/></td>
      <td className="px-4 py-3 sticky right-0 bg-white shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.12)] lg:shadow-none">
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="outline" className="h-8" onClick={() => onRequests(c)} data-testid={`portal-requests-btn-${c.id}`}><MessageSquare size={13} className="mr-1"/>Requests</Button>
          <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={copy} title="Copy portal link" data-testid={`portal-copy-${c.id}`}>{copied ? <Check size={14} className="text-emerald-700"/> : <Copy size={14}/>}</Button>
          <Button size="sm" variant="outline" className="h-8 w-8 p-0" asChild title="Open portal"><a href={url} target="_blank" rel="noreferrer" data-testid={`portal-open-link-${c.id}`}><ExternalLink size={14}/></a></Button>
        </div>
      </td>
    </tr>
  );
};

export default function Portals() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const user = getStoredUser();
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [sheet, setSheet] = useState({ open: false, client: null, focusId: null });
  const load = useCallback(() => api.get('/portals').then((r) => setData(r.data)), []);
  useEffect(() => { if (!localStorage.getItem('ad_token')) { nav('/login'); return; } load(); }, [load, nav]);
  useEffect(() => {
    const rid = params.get('request');
    if (rid) { setSheet({ open: true, client: null, focusId: rid }); setParams({}, { replace: true }); }
  }, [params, setParams]);

  const onManager = async (c, managerId) => {
    try { await api.put(`/clients/${c.id}/manager`, { managerId }); toast({ title: 'Assigned team member updated' }); load(); } catch { toast({ title: 'Could not update', variant: 'destructive' }); }
  };
  const rows = useMemo(() => (data?.clients || []).filter((c) => c.name.toLowerCase().includes(q.toLowerCase())), [data, q]);
  const tot = (k) => (data?.clients || []).reduce((a, c) => a + c[k], 0);

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Client Portals"/>
      <div className="flex-1 min-w-0">
        <Topbar user={user} firm={user?.firm}/>
        <main className="p-4 lg:p-8 space-y-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-serif font-semibold text-stone-900 flex items-center gap-2"><Globe size={24} className="text-emerald-800"/> Client Portals</h1>
              <p className="mt-1 text-stone-600 text-sm">Every client's portal in one place: who visited, what they sent and what's waiting on you.</p>
            </div>
            <Button className="bg-emerald-800 hover:bg-emerald-900" onClick={() => setSheet({ open: true, client: null, focusId: null })} data-testid="all-requests-btn"><MessageSquare size={15} className="mr-1.5"/>All requests</Button>
          </div>
          {!data ? <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-stone-400"/></div> : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="portals-summary">
                {[['Portals', data.clients.length], ['Open requests', tot('openRequests')], ['Unread replies', tot('unreadMessages')], ['Uploads this week', tot('newUploads')]].map(([l, n]) => (
                  <div key={l} className="rounded-xl bg-white border border-stone-200 p-4"><div className="text-xs text-stone-500">{l}</div><div className="text-2xl font-semibold text-stone-900 tabular-nums mt-1">{n}</div></div>
                ))}
              </div>
              <div className="relative max-w-sm"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients" className="pl-9" data-testid="portals-search"/></div>
              <div className="rounded-xl bg-white border border-stone-200 overflow-x-auto" data-testid="portals-table">
                <table className="w-full text-sm min-w-[980px]">
                  <thead className="bg-stone-50 text-xs uppercase tracking-wide text-stone-500"><tr>
                    <th className="text-left px-4 py-3">Client</th><th className="text-left px-4 py-3">Assigned to</th><th className="text-left px-4 py-3">Last visit</th><th className="text-left px-4 py-3">Last activity</th>
                    <th className="px-4 py-3">Open requests</th><th className="px-4 py-3">Unread</th><th className="px-4 py-3">Uploads (7d)</th><th className="px-4 py-3"/>
                  </tr></thead>
                  <tbody>{rows.map((c) => <Row key={c.id} c={c} team={data.team} onManager={onManager} onRequests={(cl) => setSheet({ open: true, client: cl, focusId: null })}/>)}</tbody>
                </table>
                {rows.length === 0 && <div className="p-8 text-center text-sm text-stone-500">No clients found.</div>}
              </div>
            </>
          )}
          <RequestsSheet open={sheet.open} client={sheet.client} focusId={sheet.focusId} onOpenChange={(v) => setSheet((s) => ({ ...s, open: v }))} onChanged={load}/>
        </main>
      </div>
    </div>
  );
}
